/**
 * Whether a field says what the dropped files said, and whether it still does.
 *
 * Derived from the parse rather than recorded when the seed ran: the wizard
 * re-parses on resume but never re-seeds, so anything remembered at seed time
 * is missing from every edit of an existing project — which is most of them.
 */
export type AutofillState = 'auto' | 'edited' | null;

/** What the parsers alone would put in each field, keyed as the form is. An
 * empty answer means they had nothing to say. */
export type ParserAnswers = Record<string, string>;

export const asText = (v: unknown): string =>
  Array.isArray(v) ? v.join(',') : String(v ?? '');

/**
 * The badge one field — or a group shown under a single label — earns.
 * `edited` wins inside a group: a job size with a hand-typed width is no longer
 * what the design file said, even though its other two still are.
 */
export function autofillState(
  answers: ParserAnswers,
  entries: Array<[string, unknown]>
): AutofillState {
  let state: AutofillState = null;
  entries.forEach(([key, value]) => {
    const said = answers[key];
    if (!said) return;
    if (said !== asText(value)) state = 'edited';
    else if (state === null) state = 'auto';
  });
  return state;
}

/** Job details fields the parsers can answer. Anything outside this list is the
 * user's alone and never wears a badge. */
export const JOB_DETAIL_SEED_FIELDS = [
  'width',
  'height',
  'thickness',
  'jobType',
  'zZeroPosition',
  'xyDatumPosition',
  'softwareUsed',
  'toolSetter',
  'autoToolChanger',
] as const;

export const bitAnswerKey = (opId: string) => `bit:${opId}`;
export const rpmAnswerKey = (opId: string) => `rpm:${opId}`;
